// components/playground/Header/ScenarioDropdown.jsx
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const ScenarioDropdown = ({
  currentScenarioName,
  hasUnsavedChanges,
  scenarios,
  onSelectScenario,
  onNewScenario,
  isMobile = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (scenarioId) => {
    console.log('🎯 [ScenarioDropdown] handleSelect called with:', scenarioId);
    console.log('📋 Current scenario:', currentScenarioName);
    console.log('💾 Has unsaved changes:', hasUnsavedChanges);
    
    setIsOpen(false);
    
    console.log('📞 [ScenarioDropdown] Calling onSelectScenario...');
    onSelectScenario(scenarioId);
  };

  const handleNewScenario = () => {
    console.log('✨ [ScenarioDropdown] New scenario clicked');
    setIsOpen(false);
    onNewScenario();
  };

  return (
    <div className={`relative ${isMobile ? 'w-full' : ''}`} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => {
          console.log('🔽 [ScenarioDropdown] Toggle dropdown:', !isOpen);
          setIsOpen(!isOpen);
        }}
        className={`
          flex items-center gap-2 px-3 py-2 text-sm font-medium 
          border border-gray-300 rounded-md bg-white hover:bg-gray-50 
          transition-colors text-gray-700
          ${isMobile ? 'w-full justify-between' : 'min-w-[200px]'}
        `}
      >
        <span className="flex-1 text-left truncate">
          <span className={isMobile ? 'hidden sm:inline' : ''}>Scenario: </span>
          {currentScenarioName}
        </span>
        {hasUnsavedChanges && (
          <span 
            className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"
            title="Unsaved changes"
          />
        )}
        <ChevronDownIcon 
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {isMobile && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-25 z-40"
              onClick={() => setIsOpen(false)}
            />
          )}
          
          <div className={`
            ${isMobile 
              ? 'fixed left-0 right-0 bottom-0 z-50 rounded-t-2xl max-h-[70vh]' 
              : 'absolute top-full left-0 mt-1 z-50 max-h-[400px]'
            }
            ${isMobile ? 'w-full' : 'w-full min-w-[280px]'}
            bg-white border border-gray-200 shadow-lg overflow-y-auto
            ${!isMobile && 'rounded-lg'}
          `}>
            {isMobile && (
              <div className="flex justify-center py-2 border-b border-gray-200">
                <div className="w-12 h-1 bg-gray-300 rounded-full" />
              </div>
            )}

            {isMobile && (
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-gray-900">Select Scenario</h3>
              </div>
            )}

            {/* Baseline */}
            <button
              onClick={(e) => {
                console.log('🖱️ [ScenarioDropdown] Baseline clicked');
                e.stopPropagation();
                handleSelect(null);
              }}
              className={`
                w-full text-left px-4 transition-colors flex items-center gap-2
                ${isMobile ? 'py-3.5' : 'py-2.5'}
                ${!currentScenarioName || currentScenarioName === 'Baseline' 
                  ? 'bg-blue-50' 
                  : 'hover:bg-gray-50'
                }
              `}
            >
              <span className={`flex-1 ${isMobile ? 'text-base' : 'text-sm'} text-gray-900`}>
                Baseline
              </span>
              {(!currentScenarioName || currentScenarioName === 'Baseline') && (
                <span className="text-blue-600 font-bold text-sm">✓</span>
              )}
            </button>

            {scenarios.length > 0 && (
              <div className="border-t border-gray-200 my-1" />
            )}

            {/* Saved Scenarios */}
            {scenarios.length > 0 ? (
              scenarios.map((scenario) => (
                <button
                  key={scenario.scenario_id}
                  onClick={(e) => {
                    console.log('🖱️ [ScenarioDropdown] Scenario clicked:', scenario.scenario_name, scenario.scenario_id);
                    e.stopPropagation();
                    handleSelect(scenario.scenario_id);
                  }}
                  className={`
                    w-full text-left px-4 transition-colors flex items-center gap-2
                    ${isMobile ? 'py-3.5' : 'py-2.5'}
                    ${currentScenarioName === scenario.scenario_name 
                      ? 'bg-green-50' 
                      : 'hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`${isMobile ? 'text-base' : 'text-sm'} text-gray-900 truncate`}>
                      {scenario.scenario_name}
                    </div>
                    {scenario.scenario_description && (
                      <div className={`${isMobile ? 'text-sm' : 'text-xs'} text-gray-500 truncate mt-0.5`}>
                        {scenario.scenario_description}
                      </div>
                    )}
                  </div>
                  {currentScenarioName === scenario.scenario_name && (
                    <span className="text-green-600 font-bold text-sm flex-shrink-0">✓</span>
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                No saved scenarios yet
              </div>
            )}

            <div className="border-t border-gray-200 my-1" />

            {/* New Scenario */}
            <button
              onClick={handleNewScenario}
              className={`
                w-full text-left px-4 hover:bg-blue-50 transition-colors 
                text-blue-600 flex items-center gap-2
                ${isMobile ? 'py-3.5 mb-2' : 'py-2.5'}
              `}
            >
              <span className={isMobile ? 'text-lg' : 'text-sm'}>+</span>
              <span className={isMobile ? 'text-base font-medium' : 'text-sm'}>
                New Scenario
              </span>
            </button>

            {isMobile && (
              <div className="h-safe-area-bottom" />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ScenarioDropdown;